import { ReactNode, RefObject, useContext, useEffect, useRef } from "react";
import { DispSize, WindowContext } from "./WindowContextProvider";

export interface ContentWidgetProps {
    title: string,
    hh: number,
    scale: number,
    imageClass: string,
    children: ReactNode | ReactNode[],
};

function titleBar(ctx: CanvasRenderingContext2D, mmdw: number, hh: number, topLine: boolean) {
    ctx.beginPath();
    ctx.moveTo(mmdw, hh);
    ctx.lineTo(hh * 2, hh);
    ctx.lineTo(hh * 1.5, hh / 2);
    ctx.lineTo(0, hh / 2);
    ctx.lineTo(hh * 1.5, hh / 2);
    ctx.lineTo(hh * 2, 0);
    if(topLine) ctx.lineTo(mmdw, 0);
}

export const ContentWidget = ({title,hh,scale,imageClass,children}: ContentWidgetProps) : React.JSX.Element => {
    const ds: DispSize = useContext(WindowContext);
    const hgref: RefObject<HTMLCanvasElement | null> = useRef(null);
    const divref: RefObject<HTMLDivElement | null> = useRef(null);
    var mmdw = 0, mmdh = 0;
    
    const paintContentWidget = () => {
        const ctx = hgref.current?.getContext('2d');
        if(divref.current) {
            mmdw = divref.current.clientWidth;
            mmdh = divref.current.clientHeight;
        }
        if(ctx !== null && ctx !== undefined) {
            ctx.clearRect(0, 0, mmdw, mmdh);
            ctx.imageSmoothingEnabled = true; 

            const gradient = ctx.createLinearGradient(hh*1.5, 0, mmdw, 0);
            gradient.addColorStop(0, 'rgb(9, 90, 100)');
            gradient.addColorStop(0.9, 'rgba(9, 90, 100, 0.6)');
            gradient.addColorStop(1, 'rgba(9, 90, 100, 0)');
            ctx.fillStyle = gradient;
            titleBar(ctx, mmdw, hh, true);
            ctx.fill();

            ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
            ctx.lineWidth = 3;
            titleBar(ctx, mmdw, hh, false);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(hh / 4, hh / 2);
            ctx.lineTo(hh / 4, mmdh);
            ctx.stroke();

            ctx.fillStyle = 'rgba(108, 224, 250, 0.85)';
            ctx.fillStyle = 'rgba(181, 238, 251, 0.85)';
            ctx.beginPath();
            ctx.moveTo(mmdw, hh);
            ctx.lineTo(hh * 2, hh);
            ctx.lineTo(hh * 1.5, hh / 2);
            ctx.lineTo(hh / 4, hh / 2);            
            ctx.lineTo(hh / 4, mmdh);
            ctx.lineTo(mmdw, mmdh);
            ctx.lineTo(mmdw, hh);
            ctx.fill();

            var fs = 0.027*scale*ds.height;
            if(fs < 1.3*16) fs = 1.3*16;
            if(fs > 0.05*ds.height) fs = 0.05*ds.height;
            ctx.font = `bold ${fs}px KoHo`;
            ctx.fillStyle = 'white';

            var eimt1 = ctx.measureText(title);
            var fsw1 = Math.abs(eimt1.actualBoundingBoxLeft) + Math.abs(eimt1.actualBoundingBoxRight);
            var fsh1 = Math.abs(eimt1.actualBoundingBoxAscent) + Math.abs(eimt1.actualBoundingBoxDescent);
            while(fsw1 > (mmdw-2*hh) && fs > 0) {
                fs-=2;
                ctx.font = `bold ${fs}px KoHo`;
                eimt1 = ctx.measureText(title);
                fsw1 = Math.abs(eimt1.actualBoundingBoxLeft) + Math.abs(eimt1.actualBoundingBoxRight);
                fsh1 = Math.abs(eimt1.actualBoundingBoxAscent) + Math.abs(eimt1.actualBoundingBoxDescent);
            }
            ctx.fillText(title, hh*2, hh/2+fs/2);
        }
    };
    
    useEffect(() => {
        if(hgref.current && divref.current) {
            hgref.current.width = divref.current.clientWidth;
            hgref.current.height = divref.current.clientHeight;
        }
        paintContentWidget();
    }, [ds]);

    return (
        <div style={{position: "absolute", left: 0, top: `${hh}px`, width: '100%', height: `${ds.height-hh}px`,
            display: "block", backgroundColor: 'transparent', overflowX: "hidden", overflowY: "auto",
            margin: 0, padding: 0}}>
            <img className={imageClass} style={{left: `${hh/4}px`, top: `${hh/4}px`, width: `${hh*1.5}px`,
                height: `${hh*1.5}px`, borderRadius: `${(hh*1.5)/2}px`, zIndex: 10}} />
            <div style={{margin: `${hh/3}px`, backgroundColor: 'transparent', zIndex: 11, display: "flex",
                minHeight: `${4*hh}px`}} ref={divref}>
                <canvas style={{margin: `${hh/3}px`, position: "absolute", left: 0, top: 0}} ref={hgref} />
                <div className="Content" style={{flex: 1, zIndex: 50, color: 'black', 
                    margin: `${hh*1.5}px ${hh/3}px ${hh/3}px ${hh/2}px`, font: `clamp(1.3rem, ${2.7*scale}vh, 5vh) KoHo`}}>
                    {children}
                </div>
            </div>
        </div>
    );
}